import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartType } from "./ChartTypeMenu";

export interface OverviewPoint {
  label: string;
  value: number;
  color?: string;
}
const palette = [
  "var(--color-primary)",
  "var(--color-info)",
  "var(--color-success)",
  "var(--color-warning)",
  "var(--color-danger)",
];
const tooltipStyle = {
  background: "var(--color-surface-elevated)",
  border: "1px solid var(--color-border)",
  borderRadius: 10,
  color: "var(--color-text-primary)",
  fontSize: 12,
};
export function OverviewChart({
  type,
  data,
  name,
  formatLabel,
  formatValue,
  center,
}: {
  type: ChartType;
  data: OverviewPoint[];
  name: string;
  formatLabel?: (label: string) => string;
  formatValue?: (value: number) => string;
  center?: React.ReactNode;
}) {
  if (type === "pie" || type === "donut") {
    const slices = data.filter((d) => d.value > 0);
    if (!slices.length)
      return (
        <div className="flex h-full items-center justify-center text-xs text-text-muted">
          No data to display for this period.
        </div>
      );
    return (
      <div className="relative h-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius={type === "donut" ? "66%" : 0}
              outerRadius="90%"
              paddingAngle={slices.length > 1 ? 2 : 0}
              stroke="none"
              isAnimationActive={false}
            >
              {slices.map((d, i) => (
                <Cell
                  key={d.label}
                  fill={d.color ?? palette[i % palette.length]}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(v, n) => [
                formatValue ? formatValue(Number(v)) : v,
                formatLabel ? formatLabel(String(n)) : n,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
        {type === "donut" && center && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            {center}
          </div>
        )}
      </div>
    );
  }
  const chartProps = {
    data,
    margin: { top: 8, right: 8, bottom: 0, left: -26 },
    accessibilityLayer: true,
  };
  const axes = (
    <>
      <CartesianGrid
        vertical={false}
        stroke="var(--color-border)"
        strokeDasharray="3 4"
      />
      <XAxis
        dataKey="label"
        tickFormatter={formatLabel}
        tick={{ fontSize: 10, fill: "var(--color-text-muted)" }}
        tickLine={false}
        axisLine={false}
        minTickGap={25}
      />
      <YAxis
        allowDecimals={!!formatValue}
        tick={{ fontSize: 10, fill: "var(--color-text-muted)" }}
        tickLine={false}
        axisLine={false}
      />
      <Tooltip
        contentStyle={tooltipStyle}
        cursor={{ stroke: "var(--color-border-strong)" }}
        formatter={
          formatValue ? (value) => formatValue(Number(value)) : undefined
        }
        labelFormatter={(v) =>
          formatLabel ? formatLabel(String(v)) : String(v)
        }
      />
    </>
  );
  return (
    <ResponsiveContainer width="100%" height="100%">
      {type === "bar" ? (
        <BarChart {...chartProps}>
          {axes}
          <Bar
            dataKey="value"
            name={name}
            fill="var(--color-primary)"
            radius={[4, 4, 0, 0]}
            maxBarSize={26}
            isAnimationActive={false}
          >
            {data.map((d) => (
              <Cell key={d.label} fill={d.color ?? "var(--color-primary)"} />
            ))}
          </Bar>
        </BarChart>
      ) : type === "line" ? (
        <LineChart {...chartProps}>
          {axes}
          <Line
            dataKey="value"
            name={name}
            stroke="var(--color-primary)"
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
        </LineChart>
      ) : (
        <AreaChart {...chartProps}>
          {axes}
          <Area
            dataKey="value"
            name={name}
            stroke="var(--color-primary)"
            fill="var(--color-primary-soft)"
            strokeWidth={2.5}
            isAnimationActive={false}
          />
        </AreaChart>
      )}
    </ResponsiveContainer>
  );
}
